import { useLocalStorage } from "@elit-hair/elit-tools";
import { Fab } from "@mui/material";
import { DateTime } from "luxon";
import { motion, useSpring, useTransform } from "motion/react";
import { enqueueSnackbar } from "notistack";
import { createElement, useEffect, useMemo, useRef, useState } from "react";
import { TbBriefcase, TbCalendarOff, TbPlayerPlay } from "react-icons/tb";
import { z } from "zod";

import { LocaleStorageKeys } from "@/constants";
import { DRIVER_STATUS } from "@/constants/status";
import { calculateDiff } from "@/pages/WelcomeTeam";
import ChooseShift from "@/pages/WelcomeTeam/components/ChooseShift";
import CompleteShift from "@/pages/WelcomeTeam/components/CompleteShift";
import StartShift from "@/pages/WelcomeTeam/components/StartShift";
import FreeDayDialog from "@/pages/WelcomeTeam/components/dialogs/FreeDayDialog";
import useAuth from "@/providers/hooks/useAuth";
import { useGetDailyStatusCurrentShift } from "@/requests/daily-status.queries";
import { useShiftActions, useShiftShowFloatingActionButton } from "@/stores/use-shift";
import { DriverStatus } from "@/types/daily-status.types";
import { UserType } from "@/types/user.types";

const shiftNotifySchema = z.object({
    ask: z.boolean(),
    date: z.string().datetime({ offset: true }),
});
type ShiftNotify = z.infer<typeof shiftNotifySchema>;
const getDefaultShiftNotify = () => ({
    ask: true,
    date: DateTime.now().minus({ hours: 4 }).toISO(),
});

function ShiftPopups() {
    const { userData } = useAuth();
    const { data, isPending, refetch } = useGetDailyStatusCurrentShift();
    const currentShift = useMemo(() => (data ? data.shift || undefined : undefined), [data]);
    const userType = useMemo(() => (data ? data.userType : false), [data]);

    const [lastShiftNotify, setLastShiftNotify] = useLocalStorage<ShiftNotify>(
        LocaleStorageKeys.SHIFT_NOTIFY,
        getDefaultShiftNotify()
    );

    const {
        updateShiftDecision,
        updateShowStartShiftConfirmation,
        updateShowStartFreeDayConfirmation,
        updateShowCompleteShiftConfirmation,
    } = useShiftActions();

    const handleStartShift = (val?: boolean, request?: boolean) => {
        updateShiftDecision();
        updateShowStartShiftConfirmation(val);
        if (request) {
            refetch();
        }
    };

    const handleCompleteShiftClose = (request?: boolean) => {
        if (request) {
            setLastShiftNotify({ date: DateTime.now().toISO(), ask: true });
            refetch();
        }
        updateShowCompleteShiftConfirmation(false);
    };

    const handleFreeDay = (val?: boolean, request?: boolean) => {
        updateShiftDecision();
        updateShowStartFreeDayConfirmation(val);
        if (request) {
            refetch();
        }
    };

    const handleStopShift = () => {
        if (!currentShift || !currentShift.startTime) return;
        const diff = calculateDiff(currentShift.startTime);
        if (!diff) return;
        else if (currentShift.status === DRIVER_STATUS.WORKING && (!diff[0] || diff[0] < 1)) {
            enqueueSnackbar("Mesai 1 saatten erken bitirilemez.", { variant: "error" });
            return;
        } else if (
            ![DRIVER_STATUS.WORKING, DRIVER_STATUS.FREE_DAY].includes(currentShift.status as "WORKING" | "FREE_DAY")
        ) {
            enqueueSnackbar(
                "Haftalık izin dışındaki durumları bitirmek için lütfen yöneticiniz ile iletişime geçin." +
                    (currentShift.endTime
                        ? "İzin bitiş tarihi:" + currentShift.endTime.toFormat("yyyy-MM-dd HH:mm")
                        : ""),
                {
                    variant: "error",
                }
            );
            return;
        }
        if (currentShift.status === DRIVER_STATUS.WORKING) updateShowCompleteShiftConfirmation(true);
        else handleStartShift(true);
    };

    useEffect(() => {
        if (userType && userType !== UserType.DRIVER && !isPending && !!data && !currentShift) {
            const res = shiftNotifySchema.safeParse(lastShiftNotify);

            if (!res.success) {
                setLastShiftNotify(getDefaultShiftNotify());
                return;
            }
            const { ask, date } = res.data;
            if (!ask) return;
            const lastDate = DateTime.fromISO(date);
            const now = DateTime.now().plus({ minutes: 1 });

            if (lastDate && now.diff(lastDate).as("hours") > 4) {
                setLastShiftNotify({ date: now.toISO(), ask: true });
                updateShiftDecision(true);
            }
        }
    }, [updateShiftDecision, lastShiftNotify, userType, isPending, currentShift, data]);
    const shiftShowFloatingActionButton = useShiftShowFloatingActionButton();

    return userData ? (
        <>
            <ChooseShift handleStartShift={handleStartShift} handleFreeDay={handleFreeDay} />
            <StartShift shiftExists={!!currentShift} handleClose={handleStartShift} />
            <CompleteShift currentShift={currentShift} handleClose={handleCompleteShiftClose} />
            <FreeDayDialog handleClose={handleFreeDay} />

            {!isPending && shiftShowFloatingActionButton && userType && userType !== UserType.DRIVER && (
                <FloationButton
                    handleStartShift={updateShiftDecision}
                    handleStopShift={handleStopShift}
                    currentShift={currentShift}
                />
            )}
        </>
    ) : null;
}

export function AnimatedNumber({ value, symbol }: { value: number; symbol: string }) {
    let spring = useSpring(value, { mass: 0.8, stiffness: 75, damping: 15 });
    let display = useTransform(spring, (current) => Math.round(current).toLocaleString() + symbol);

    useEffect(() => {
        spring.set(value);
    }, [spring, value]);

    return <motion.span>{display}</motion.span>;
}

const MotionFab = motion(Fab);

function FloationButton({
    currentShift,
    handleStopShift,
    handleStartShift,
}: {
    handleStopShift: () => void;
    currentShift: DriverStatus | undefined | false;
    handleStartShift: (val?: boolean) => void;
}) {
    const intervalId = useRef<ReturnType<typeof setInterval> | null>(null);
    const [timeDiff, setTimeDiff] = useState<[number, number, number] | null>(null);

    useEffect(() => {
        const cb = () => {
            if (!currentShift) {
                setTimeDiff(null);
                return;
            }
            const res = calculateDiff(currentShift.startTime);
            if (res) {
                setTimeDiff(res);
            }
        };

        intervalId.current = setInterval(cb, 4000);
        cb();

        return () => {
            if (intervalId.current) clearInterval(intervalId.current);
        };
    }, [currentShift]);

    return currentShift ? (
        <MotionFab
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={handleStopShift}
            color={timeDiff && timeDiff[0] >= 1 ? "success" : "warning"}
            variant="extended"
            size="small"
            className="!fixed !font-medium !text-xs !flex !gap-1   !lowercase !bottom-0 !right-0  !m-4 print:!hidden"
        >
            {createElement(currentShift.status === DRIVER_STATUS.WORKING ? TbBriefcase : TbCalendarOff, {
                fontSize: 16,
            })}
            {timeDiff && (
                <>
                    <AnimatedNumber value={timeDiff[0]} symbol="h " />
                    <AnimatedNumber value={timeDiff[1]} symbol="m" />
                </>
            )}
        </MotionFab>
    ) : (
        <MotionFab
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => handleStartShift(true)}
            color="primary"
            variant="extended"
            size="small"
            className="!fixed !font-medium !capitalize !text-xs !flex !gap-1 !bottom-0 !right-0  !m-4 print:!hidden"
        >
            <TbPlayerPlay />
            Mesai
        </MotionFab>
    );
}

export default ShiftPopups;
